import {
  Box,
  Flex,
  Icon,
  IconButton,
  Select,
  Spinner,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useContext, useEffect, useRef, useState } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { MdOutlinePreview, MdRefresh } from "react-icons/md";
import IframeWidget from "../Playground/IframeWidget";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";

const RENDERABLE_TYPES = ["chat", "web_call", "html_call"];

export default function AssistantPreviewPanel({ assistantId, assistantType, assistantName, saveCount = 0 }) {
  const { myDetails } = useContext(AgencyContext);
  const baseUrl = getHostDomain();
  const { control } = useFormContext();
  const { isDirty } = useFormState({ control });

  const [widgets, setWidgets] = useState([]);
  const [selectedWidget, setSelectedWidget] = useState(null);
  // Ref updated synchronously during render — true once the form has been
  // seen clean (isDirty=false) at least once for the current assistant.
  // Using a ref avoids async setState batching issues that could prevent the
  // flag from being set before the user makes their first edit.
  const hasBeenCleanRef = useRef(false);
  const prevAssistantId = useRef(assistantId);
  if (prevAssistantId.current !== assistantId) {
    prevAssistantId.current = assistantId;
    hasBeenCleanRef.current = false;
  }
  if (!isDirty) hasBeenCleanRef.current = true;
  const showDirtyOverlay = isDirty && hasBeenCleanRef.current;

  const [iframeKey, setIframeKey] = useState(0);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!assistantId) return;
    let cancelled = false;

    async function init() {
      setStatus("loading");
      try {
        const res = await fetchController(
          `${baseUrl}/api/v1/assistant/${assistantId}/widgets?size=50`,
          "GET"
        );
        if (cancelled) return;
        const items = (res?.data?.items || []).filter((w) =>
          RENDERABLE_TYPES.includes(w.widget_type)
        );

        if (items.length > 0) {
          setWidgets(items);
          setSelectedWidget(items[0]);
          setStatus("ready");
        } else {
          setStatus("creating");
          const widgetType = assistantType === "simple" ? "chat" : "web_call";
          const created = await fetchController(
            `${baseUrl}/api/v1/widget`,
            "POST",
            {
              name: `${assistantName || "Assistant"} Preview`,
              description: "Auto-created preview widget",
              widget_type: widgetType,
              assistant_id: assistantId,
              attributes: {},
              user_opening_messages: [],
              action_buttons: [],
              style_params: {
                agency_name: myDetails?.branding?.agency_name || "Insighto",
                agency_app_domain: myDetails?.domain?.appDomain || "",
                agency_logo_url: myDetails?.branding?.agency_logo || "",
              },
            }
          );
          if (cancelled) return;
          const newWidget = created?.data;
          if (newWidget?.id) {
            setWidgets([newWidget]);
            setSelectedWidget(newWidget);
            setStatus("ready");
          } else {
            setStatus("error");
          }
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    }

    init();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantId]);

  // Auto-refresh whenever the assistant is saved
  useEffect(() => {
    if (saveCount > 0) setIframeKey((k) => k + 1);
  }, [saveCount]);

  // ── Grayed-out placeholder when creating a new assistant ──────────────────
  if (!assistantId) {
    return (
      <Box
        borderRadius="xl"
        overflow="hidden"
        boxShadow="md"
        border="1px solid"
        borderColor="gray.100"
        bg="gray.50"
        opacity={0.65}
        pointerEvents="none"
        userSelect="none"
      >
        <Flex
          px={4}
          py={3}
          bg="gray.100"
          borderBottom="1px solid"
          borderColor="gray.200"
          align="center"
          justify="space-between"
        >
          <Text fontWeight="semibold" fontSize="sm" color="gray.400">
            Preview
          </Text>
        </Flex>
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap={3}
          py={20}
          px={6}
        >
          <Icon as={MdOutlinePreview} boxSize={10} color="gray.300" />
          <Text fontSize="sm" color="gray.400" textAlign="center">
            Save the assistant first to enable the live preview
          </Text>
        </Flex>
      </Box>
    );
  }

  // ── Live preview ──────────────────────────────────────────────────────────
  return (
    <Box
      borderRadius="xl"
      overflow="hidden"
      boxShadow="lg"
      border="1px solid"
      borderColor="gray.100"
      bg="white"
    >
      {/* Header */}
      <Flex
        px={4}
        py={3}
        bg="gray.50"
        borderBottom="1px solid"
        borderColor="gray.100"
        justify="space-between"
        align="center"
      >
        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
          Preview
        </Text>
        <Flex gap={2} align="center">
          {widgets.length > 1 && (
            <Select
              size="xs"
              maxW="200px"
              value={selectedWidget?.id || ""}
              onChange={(e) => {
                const w = widgets.find((x) => x.id === e.target.value);
                if (w) { setSelectedWidget(w); setIframeKey((k) => k + 1); }
              }}
            >
              {widgets.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name} ({w.widget_type})
                </option>
              ))}
            </Select>
          )}
          <Tooltip label="Refresh preview">
            <IconButton
              size="xs"
              variant="ghost"
              icon={<MdRefresh size={16} />}
              aria-label="Refresh preview"
              onClick={() => setIframeKey((k) => k + 1)}
              isDisabled={status !== "ready"}
            />
          </Tooltip>
        </Flex>
      </Flex>

      {/* Body */}
      {(status === "loading" || status === "creating") && (
        <Flex justify="center" align="center" direction="column" gap={2} py={16}>
          <Spinner size="sm" color="gray.400" />
          {status === "creating" && (
            <Text fontSize="xs" color="gray.400">Setting up preview…</Text>
          )}
        </Flex>
      )}

      {status === "error" && (
        <Flex justify="center" align="center" py={16}>
          <Text fontSize="sm" color="red.400">Could not load preview.</Text>
        </Flex>
      )}

      {status === "ready" && selectedWidget && (
        <Box position="relative">
          <IframeWidget
            key={iframeKey}
            widgetId={selectedWidget.id}
            widgetType={selectedWidget.widget_type}
            height="600px"
          />
          {showDirtyOverlay && (
            <Flex
              position="absolute"
              inset={0}
              bg="whiteAlpha.700"
              backdropFilter="blur(2px)"
              align="center"
              justify="center"
            >
              <Text fontSize="sm" color="gray.500" fontStyle="italic">
                Save to refresh preview
              </Text>
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
}
